import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { Loader2, CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// UI Components
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Textarea } from '@/components/ui/textarea';

// Use purchase-specific components
import { PurchaseInvoiceDataGrid, GridColumn, ProductSearchResult } from '../components/PurchaseInvoiceDataGrid';

// Types
import { Party } from '@/app/types/party';
import { Site } from '@/app/types/site';
import { Item } from '@/app/types/item';
import { Godown } from '@/app/types/inventory';
import { VoucherResponse } from '@/app/types/voucher';
import { PurchaseInvoiceFormValues, purchaseInvoiceSchema } from './schema';
import { getPurchaseItemPricingDetails } from '@/app/lib/invoiceCalculations';
import { Decimal } from 'decimal.js';
import { useFormHotkeys } from '@/app/hooks/useFormHotkeys';
import { usePreferenceStore } from '@/app/store/preferenceStore';
import { PartySelector } from '@/app/components/common/PartySelector';

type CalculationItem = { 
    quantity?: number | string | null, 
    rate?: number | string | null, 
    discount_percent?: number | string | null 
};

interface PurchaseInvoiceFormProps {
  purchaseInvoice?: VoucherResponse;
  parties: Party[];
  sites: Site[];
  godowns: Godown[];
  voucherTypeId: number;
  isSubmitting: boolean;
  onSubmit: (data: PurchaseInvoiceFormValues) => void;
  searchItems: (query: string) => Promise<Item[]>;
  isLoadingItems: boolean;
  isDisabled?: boolean;
}

export const PurchaseInvoiceForm: React.FC<PurchaseInvoiceFormProps> = ({
  purchaseInvoice,
  parties,
  sites,
  godowns,
  voucherTypeId,
  isSubmitting,
  onSubmit,
  searchItems,
  isLoadingItems,
  isDisabled = false,
}) => {
  const isEditMode = !!purchaseInvoice;
  const gridRef = useRef<HTMLDivElement>(null);
  const [lineItems, setLineItems] = useState<Record<string, unknown>[]>([]);
  
  // Get user preferences
  const { defaultSiteId, defaultGodownId: preferenceGodownId } = usePreferenceStore();

  // Default Godown selection
  const [defaultGodownId, setDefaultGodownId] = useState<number>(
    godowns.length > 0 ? (
      // Use preference godown if available, otherwise first godown
      preferenceGodownId && godowns.some(g => g.id === preferenceGodownId) 
        ? preferenceGodownId 
        : godowns[0].id
    ) : 0
  );

  // Form initialization
  const defaultValues: Partial<PurchaseInvoiceFormValues> = {
    voucher_type_id: voucherTypeId,
    transaction_date: new Date(),
    due_date: null,
    total_amount: 0,
    discount_type: null,
    discount_value: null,
    reference_number: '',
    items: [],
    // Use preference site if available
    site_id: defaultSiteId && sites.some(s => s.id === defaultSiteId) ? defaultSiteId : undefined,
  };

  const form = useForm<PurchaseInvoiceFormValues>({
    resolver: zodResolver(purchaseInvoiceSchema),
    defaultValues: purchaseInvoice
      ? {
          voucher_type_id: purchaseInvoice.voucher_type_id,
          party_id: purchaseInvoice.party_id || undefined,
          site_id: purchaseInvoice.site_id || undefined,
          transaction_date: purchaseInvoice.transaction_date ? new Date(purchaseInvoice.transaction_date) : new Date(),
          due_date: purchaseInvoice.due_date ? new Date(purchaseInvoice.due_date) : null,
          reference_number: purchaseInvoice.reference_number || '',
          narration: purchaseInvoice.narration || '',
          total_amount: parseFloat(String(purchaseInvoice.total_amount || 0)),
          discount_type: purchaseInvoice.discount_type || null,
          discount_value: purchaseInvoice.discount_value ? parseFloat(String(purchaseInvoice.discount_value)) : null,
          items: [],
        }
      : defaultValues,
  });

  // Initialize with empty row for new invoices
  useEffect(() => {
    if (!isEditMode && lineItems.length === 0 && godowns.length > 0) {
      const initialGodownId = defaultGodownId || godowns[0]?.id;
      if (initialGodownId) {
        console.log('[PurchaseInvoiceForm] Initializing with godown_id:', initialGodownId);
        setLineItems([{
          item_id: undefined,
          item_name: '',
          item_code: '',
          description: '',
          hsn_code: '',
          tax_percent: '-',
          quantity: 1,
          rate: 0,
          discount_percent: 0,
          discount_amount: 0,
          amount: 0,
          godown_id: initialGodownId,
          tax_rate_id: null,
          taxable_amount: 0,
          tax_amount: 0,
          is_tax_inclusive: true, // For now, consider all items as tax-inclusive
          discounted_rate: 0,
        }]);
      }
    }
  }, [isEditMode, defaultGodownId, godowns.length]); // Don't include lineItems to avoid infinite loop

  // Calculation Helpers
  const calculateDiscountAmount = useCallback((item: CalculationItem): number => {
    const quantity = parseFloat(String(item.quantity || 0));
    const rate = parseFloat(String(item.rate || 0));
    const discountPercent = parseFloat(String(item.discount_percent || 0));
    const grossAmount = quantity * rate;
    const discountAmount = (grossAmount * discountPercent) / 100;
    return Math.round(discountAmount * 100) / 100;
  }, []);

  const calculateFinalRowAmount = useCallback((item: CalculationItem): number => {
    const quantity = parseFloat(String(item.quantity || 0));
    const rate = parseFloat(String(item.rate || 0));
    const discountAmount = calculateDiscountAmount(item);
    const grossAmount = quantity * rate;
    return Math.round((grossAmount - discountAmount) * 100) / 100;
  }, [calculateDiscountAmount]);

  // Effect to initialize/update grid items from props or defaults
  useEffect(() => {
    const apiItems = purchaseInvoice?.items || [];
    let itemsForGrid: Record<string, unknown>[] = [];
    let itemsForForm: PurchaseInvoiceFormValues['items'] = [];
    let calculatedTotal = 0;
    
    // For existing invoices in view/edit mode, use the actual total from backend
    if (isEditMode && purchaseInvoice?.total_amount) {
      calculatedTotal = Number(purchaseInvoice.total_amount);
    }

    const effectiveDefaultGodownId = godowns.length > 0 ? godowns[0].id : null;

    if (apiItems.length > 0) {
      itemsForGrid = apiItems.map((item) => {
        const itemData = { quantity: item.quantity, rate: item.rate, discount_percent: item.discount_percent };
        const discountAmt = calculateDiscountAmount(itemData);
        const finalAmt = calculateFinalRowAmount(itemData);
        const quantity = parseFloat(String(item.quantity || 0));
        const rate = parseFloat(String(item.rate || 0));
        const discountPercent = parseFloat(String(item.discount_percent || 0));
        
        // Use API-provided values for tax and totals if available
        const discountAmount = item.discount_amount ? parseFloat(String(item.discount_amount)) : discountAmt;
        const taxableAmount = item.taxable_amount ? parseFloat(String(item.taxable_amount)) : finalAmt;
        const taxAmount = item.tax_amount ? parseFloat(String(item.tax_amount)) : 0;
        const totalAmount = item.total_amount ? parseFloat(String(item.total_amount)) : (taxableAmount + taxAmount);
        
        // Calculate tax percent from item data - check both item.tax_rate and item.item.tax_rate
        let taxPercent = '-';
        let taxRatePercent = 0;
        
        // First check if tax_rate is directly on the item (as shown in the API response)
        if (item.tax_rate && item.tax_rate.rate !== undefined) {
          taxRatePercent = parseFloat(item.tax_rate.rate);
          taxPercent = `${taxRatePercent}%`;
        } 
        // Fallback to item.item.tax_rate if available
        else if (item.tax_rate_id && item.item?.tax_rate) {
          taxRatePercent = parseFloat(item.item.tax_rate.rate) || 0;
          taxPercent = `${taxRatePercent}%`;
        }
        // If we have a numeric tax rate percent of 0, still show it as "0%"
        else if (taxRatePercent === 0) {
          taxPercent = '0%';
        }
        
        // For now, consider all items as tax-inclusive
        let isTaxInclusive = true;
        
        // Calculate discounted rate (total amount / quantity)
        let discountedRate = 0;
        if (quantity > 0) {
          discountedRate = totalAmount / quantity;
        }
        
        return {
          id: item.id,
          item_id: item.item_id,
          item_name: item.item?.name || `Item ID: ${item.item_id}`,
          item_code: item.item?.code || '',
          description: item.description || '',
          hsn_code: item.item?.hsn_code || '',
          tax_percent: taxPercent,
          quantity: quantity,
          rate: rate,
          discount_percent: discountPercent,
          discount_amount: discountAmount,
          tax_rate_id: item.tax_rate_id,
          taxable_amount: taxableAmount,
          tax_amount: taxAmount,
          amount: totalAmount,
          godown_id: item.godown_id || effectiveDefaultGodownId,
          rate_decimal_places: 2, // Use standard 2 decimal places for purchase invoices
          tax_rate_percent: taxRatePercent,
          is_tax_inclusive: isTaxInclusive,
          discounted_rate: discountedRate,
        };
      });
      itemsForForm = itemsForGrid.map(item => ({
        id: typeof item.id === 'number' ? item.id : undefined,
        item_id: typeof item.item_id === 'number' ? item.item_id : 0,
        quantity: parseFloat(String(item.quantity || 0)),
        rate: parseFloat(String(item.rate || 0)),
        discount_percent: parseFloat(String(item.discount_percent || 0)),
        discount_amount: parseFloat(String(item.discount_amount || 0)),
        tax_rate_id: typeof item.tax_rate_id === 'number' ? item.tax_rate_id : null,
        amount: parseFloat(String(item.amount || 0)),
        description: String(item.description || ''),
        godown_id: typeof item.godown_id === 'number' ? item.godown_id : defaultGodownId,
      }));
      // Only calculate total for new invoices, for existing ones use the backend total
      if (!isEditMode || !purchaseInvoice?.total_amount) {
        calculatedTotal = Math.ceil(itemsForForm.reduce((sum, item) => sum + item.amount, 0));
      }
    } else if (!isEditMode) {
      // Default row for create mode
      itemsForGrid = [{ 
        quantity: 1, 
        rate: 0, 
        discount_percent: 0, 
        discount_amount: 0, 
        amount: 0, 
        description: '', 
        godown_id: effectiveDefaultGodownId,
        discounted_rate: 0 
      }];
      itemsForForm = [];
      calculatedTotal = 0;
    }

    setLineItems(itemsForGrid);
    form.setValue('items', itemsForForm);
    form.setValue('total_amount', calculatedTotal);
  }, [purchaseInvoice, isEditMode, godowns, calculateDiscountAmount, calculateFinalRowAmount, form]);

  // Effect to update form state when lineItems (grid state) change
  // Sync lineItems to form for validation
  useEffect(() => {
    // Filter out placeholder/invalid items before updating form
    const validLineItems = lineItems.filter(item => item && typeof item.item_id === 'number' && item.item_id > 0);

    const formItems = validLineItems.map(item => {
      return {
        id: typeof item.id === 'number' ? item.id : undefined,
        item_id: typeof item.item_id === 'number' ? item.item_id : 0,
        quantity: parseFloat(String(item.quantity || 1)),
        rate: parseFloat(String(item.rate || 0)),
        discount_percent: parseFloat(String(item.discount_percent || 0)),
        discount_amount: parseFloat(String(item.discount_amount || 0)),
        tax_rate_id: item.tax_rate_id || null,
        taxable_amount: parseFloat(String(item.taxable_amount || 0)),
        tax_amount: parseFloat(String(item.tax_amount || 0)),
        amount: parseFloat(String(item.amount || 0)),
        description: String(item.description || ''),
        godown_id: typeof item.godown_id === 'number' ? item.godown_id : (defaultGodownId || godowns[0]?.id || 0),
      };
    });

    // Recalculate total amount
    const total = validLineItems.reduce((sum, item) => {
      return sum + parseFloat(String(item.amount || 0));
    }, 0);
    const roundedTotal = Math.ceil(total);

    // Update form without triggering validation on every change
    form.setValue('items', formItems, { shouldValidate: false });
    form.setValue('total_amount', roundedTotal, { shouldValidate: false });
  }, [lineItems, defaultGodownId, godowns, form]);

  // Register Cmd+S / Ctrl+S hotkey for form submission
  useFormHotkeys(() => {
    if (!isDisabled && !isSubmitting) {
      form.handleSubmit(handleSubmit)();
    }
  });

  // Column Definitions for Grid
  const columns: GridColumn[] = [
    { field: 'item_id', header: 'Product/Service', type: 'product', width: '200px', sortable: true },
    { field: 'hsn_code', header: 'HSN', type: 'text', editable: false, width: '100px', sortable: true },
    { field: 'quantity', header: 'Qty', type: 'number', width: '80px', sortable: true },
    { field: 'rate', header: 'Rate', type: 'price', width: '100px', sortable: true },
    { field: 'tax_percent', header: 'Tax %', type: 'text', editable: false, width: '80px', sortable: false },
    // Read-only calculated fields
    { field: 'taxable_amount', header: 'Taxable Amt', type: 'price', editable: false, width: '120px', sortable: false },
    { field: 'tax_amount', header: 'Tax Amt', type: 'price', editable: false, width: '100px', sortable: false },
    { field: 'amount', header: 'Amount', type: 'price', editable: false, width: '120px', sortable: true },
    { field: 'discount_percent', header: 'Disc %', type: 'number', width: '80px', sortable: false },
    { field: 'discount_amount', header: 'Disc Amt', type: 'price', width: '100px', sortable: false },
    { field: 'discounted_rate', header: 'Disc Rate', type: 'price', editable: false, width: '100px', sortable: false },
    { field: 'actions', header: '', type: 'actions', width: '50px', sortable: false },
  ];

  // Wrapper for searchItems API call
  const searchProductsWrapper = useCallback(async (searchTerm: string): Promise<ProductSearchResult[]> => {
    if (!searchTerm || searchTerm.length < 2) return [];
    try {
      const items = await searchItems(searchTerm);
      return items.map(item => ({
        id: item.id,
        name: item.name || '',
        code: item.code || null,
        description: item.description || item.name || null,
        hsn_code: item.hsn_code || null, // Include HSN code
        tax_rate_id: item.tax_rate_id || null, // Include tax_rate_id for tax calculations
        rate_decimal_places: 2, // Use standard 2 decimal places for purchase invoices
      }));
    } catch (error) {
      console.error('Error searching products:', error);
      return [];
    }
  }, [searchItems]);

  // Handle product selection from modal
  const handleProductSelect = useCallback(async (product: ProductSearchResult, rowIndex: number): Promise<void> => {
    console.log(`[PurchaseInvoiceForm] handleProductSelect for row ${rowIndex}, item ID: ${product.id}`);
    
    // Get the current quantity for the row (default to 1 if not set)
    const currentRow = lineItems[rowIndex] || {};
    const currentQuantity = new Decimal(String(currentRow.quantity || 1));
    const currentSiteId = form.watch('site_id');
    const currentPartyId = form.watch('party_id');
    
    console.log(`[TAX DEBUG] Product selection with tax_rate_id: ${product.tax_rate_id}`);

    let priceDetails: any | null = null;
    try {
      // Use purchase pricing function for purchase invoices
      const pricingResult = await getPurchaseItemPricingDetails(
        product.id,
        currentQuantity.toNumber(),
        currentSiteId,
        currentPartyId
      );
      
      if (pricingResult) {
        // Convert to the expected format
        priceDetails = {
          rate: new Decimal(pricingResult.rate),
          discount_percent: new Decimal(pricingResult.discount_percent),
          discount_amount: new Decimal(pricingResult.discount_amount),
          tax_rate_id: pricingResult.tax_rate_id,
          tax_amount: new Decimal(pricingResult.tax_amount),
          tax_breakdown: pricingResult.tax_breakdown.map(tb => ({
            code: tb.code,
            rate: new Decimal(tb.rate),
            amount: new Decimal(tb.amount)
          })),
          final_amount: new Decimal(pricingResult.amount),
          taxable_amount: new Decimal(pricingResult.taxable_amount),
          is_tax_inclusive: pricingResult.is_tax_inclusive
        };
      }
      
      console.log("[TAX DEBUG] Received priceDetails with tax info:", {
        tax_rate_id: priceDetails ? priceDetails.tax_rate_id : null,
        tax_amount: priceDetails ? priceDetails.tax_amount : null,
        final_amount: priceDetails ? priceDetails.final_amount : null,
      });
      
    } catch (error) {
      console.error("[PurchaseInvoiceForm] Error fetching price details:", error);
      toast.error(`Error calculating price for ${product.name}: ${(error as Error).message}`);
      priceDetails = null;
    }

    // Update the grid data state
    setLineItems(currentItems => {
      const newItems = [...currentItems];
      if (newItems[rowIndex]) {
        const rate = priceDetails ? priceDetails.rate.toNumber() : 0;
        const updatedRow = {
          ...newItems[rowIndex],
          item_id: product.id,
          item_name: product.name,
          item_code: product.code || '',
          description: '',
          godown_id: newItems[rowIndex].godown_id || defaultGodownId || godowns[0]?.id || 0,
          quantity: currentQuantity.toNumber(),
          rate: rate,
          discount_percent: priceDetails ? priceDetails.discount_percent.toNumber() : 0,
          discount_amount: priceDetails ? priceDetails.discount_amount.toNumber() : 0,
          taxable_amount: priceDetails ? priceDetails.taxable_amount.toNumber() : 0,
          tax_rate_id: priceDetails ? priceDetails.tax_rate_id : null,
          tax_amount: priceDetails ? priceDetails.tax_amount.toNumber() : 0,
          amount: priceDetails ? priceDetails.final_amount.toNumber() : 0,
          is_tax_inclusive: true, // For now, consider all items as tax-inclusive
          tax_rate_percent: priceDetails && priceDetails.tax_breakdown 
            ? priceDetails.tax_breakdown.reduce((sum: number, component: any) => 
                sum + component.rate.toNumber(), 0)
            : 0,
          tax_percent: (() => {
            if (priceDetails) {
              if (priceDetails.tax_breakdown) {
                const totalRate = priceDetails.tax_breakdown.reduce((sum: number, component: any) => 
                  sum + component.rate.toNumber(), 0);
                return `${totalRate}%`;
              } else if (typeof priceDetails.tax_rate_percent === 'number') {
                return `${priceDetails.tax_rate_percent}%`;
              } else if (priceDetails.tax_rate_id === null) {
                // Explicitly no tax (0% tax)
                return '0%';
              }
            }
            return '-';
          })(),
          hsn_code: product.hsn_code || '',
          rate_decimal_places: 2, // Use standard 2 decimal places for purchase invoices
          discounted_rate: (() => {
            // Calculate discounted rate from final amount and quantity
            const finalAmount = priceDetails ? priceDetails.final_amount.toNumber() : 0;
            const qty = currentQuantity.toNumber();
            return qty > 0 ? finalAmount / qty : 0;
          })(),
        };

        console.log(`[TAX DEBUG] Updated row ${rowIndex} tax data:`, {
          tax_rate_id: updatedRow.tax_rate_id,
          taxable_amount: updatedRow.taxable_amount,
          tax_amount: updatedRow.tax_amount,
          final_amount: updatedRow.amount,
        });

        newItems[rowIndex] = updatedRow;
      }
      return newItems;
    });

  }, [defaultGodownId, lineItems, form]);

  // Handle quantity change for a row
  const handleQuantityChangeForRow = async (rowIndex: number, newQuantity: number): Promise<void> => {
    console.log(`[TAX DEBUG] Quantity changed for row ${rowIndex} to ${newQuantity}.`);

    const itemInRow = lineItems[rowIndex];
    if (!itemInRow || !itemInRow.item_id) {
      console.warn(`[PurchaseInvoiceForm] Can't update quantity: No valid item in row ${rowIndex}`);
      return;
    }

    const itemId = Number(itemInRow.item_id);
    const currentSiteId = form.watch('site_id');
    const currentPartyId = form.watch('party_id');
    
    // Get current values to preserve user's manual inputs
    const currentRate = parseFloat(String(itemInRow.rate || 0));
    const currentDiscountPercent = parseFloat(String(itemInRow.discount_percent || 0));
    const currentDiscountAmount = parseFloat(String(itemInRow.discount_amount || 0));
    const currentTaxRateId = itemInRow.tax_rate_id;
    const currentTaxRatePercent = parseFloat(String(itemInRow.tax_rate_percent || 0));
    const currentIsTaxInclusive = itemInRow.is_tax_inclusive || false;

    // For purchase invoices, we typically don't have dynamic pricing rules like sales
    // So we'll just recalculate based on the current rate and quantity
    // Unless the backend provides specific purchase pricing
    
    try {
      // First check if we need to fetch new pricing (e.g., quantity-based discounts)
      // For now, let's just recalculate with existing values
      
      setLineItems(currentItems => {
        const newItems = [...currentItems];
        if (newItems[rowIndex]) {
          const updatedQuantity = newQuantity;
          const grossAmount = currentRate * updatedQuantity;
          
          // Calculate discount
          const discountAmount = currentDiscountPercent > 0 
            ? (grossAmount * currentDiscountPercent) / 100 
            : currentDiscountAmount;
          
          // Calculate tax and total
          let taxableAmount, taxAmount, totalAmount;
          
          if (currentIsTaxInclusive) {
            // Tax-inclusive: total is gross - discount, tax is extracted
            totalAmount = grossAmount - discountAmount;
            if (currentTaxRatePercent > 0) {
              taxAmount = (totalAmount * currentTaxRatePercent) / (100 + currentTaxRatePercent);
              taxableAmount = totalAmount - taxAmount;
            } else {
              taxAmount = 0;
              taxableAmount = totalAmount;
            }
          } else {
            // Tax-exclusive: tax is added on top
            taxableAmount = grossAmount - discountAmount;
            taxAmount = currentTaxRatePercent > 0 ? (taxableAmount * currentTaxRatePercent) / 100 : 0;
            totalAmount = taxableAmount + taxAmount;
          }
          
          // Calculate discounted rate
          const discountedRate = updatedQuantity > 0 ? (Math.round(totalAmount * 100) / 100) / updatedQuantity : 0;
          
          // Update the row with recalculated values
          newItems[rowIndex] = {
            ...newItems[rowIndex],
            quantity: updatedQuantity,
            taxable_amount: Math.round(taxableAmount * 100) / 100,
            tax_amount: Math.round(taxAmount * 100) / 100,
            amount: Math.round(totalAmount * 100) / 100,
            discounted_rate: discountedRate
          };
          
          console.log(`[PurchaseInvoiceForm] Updated row ${rowIndex} after quantity change:`, {
            quantity: updatedQuantity,
            rate: currentRate,
            taxable_amount: taxableAmount,
            tax_amount: taxAmount,
            total_amount: totalAmount
          });
        }
        return newItems;
      });
      
    } catch (error) {
      console.error(`[PurchaseInvoiceForm] Error recalculating totals for item ${itemId}:`, error);
      toast.error(`Error recalculating totals: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Handle form submission
  const handleSubmit = (data: PurchaseInvoiceFormValues) => {
    console.log('[handleSubmit] Called with data:', data);
    console.log('[handleSubmit] onSubmit prop:', onSubmit);
    console.log('[handleSubmit] typeof onSubmit:', typeof onSubmit);
    
    try {
      data.voucher_type_id = voucherTypeId;
      
      // Process lineItems to create proper form items
      const validLineItems = lineItems.filter(item => item && typeof item.item_id === 'number' && item.item_id > 0);
      
      const formItems = validLineItems.map(item => ({
        id: typeof item.id === 'number' ? item.id : undefined,
        item_id: typeof item.item_id === 'number' ? item.item_id : 0,
        quantity: parseFloat(String(item.quantity || 1)),
        rate: parseFloat(String(item.rate || 0)),
        discount_percent: parseFloat(String(item.discount_percent || 0)),
        discount_amount: parseFloat(String(item.discount_amount || 0)),
        tax_rate_id: typeof item.tax_rate_id === 'number' ? item.tax_rate_id : null,
        amount: parseFloat(String(item.amount || 0)),
        description: String(item.description || ''),
        godown_id: typeof item.godown_id === 'number' ? item.godown_id : defaultGodownId,
      }));
      
      // Update data with processed items
      data.items = formItems;
      
      // Filter out items with missing product IDs or zero quantity
      const validItems = data.items.filter(item => 
        item.item_id && item.quantity > 0
      );
      
      if (validItems.length === 0) {
        toast.error("Cannot submit invoice with no valid items.");
        return;
      }
      
      // Calculate subtotal from items
      const itemsSubtotal = validItems.reduce((sum, item) => sum + item.amount, 0);
      
      // Calculate discount amount
      let discountAmount = 0;
      if (data.discount_type === 'percentage' && data.discount_value) {
        discountAmount = (itemsSubtotal * data.discount_value) / 100;
      } else if (data.discount_type === 'fixed' && data.discount_value) {
        discountAmount = data.discount_value;
      }
      
      // Calculate final total after discount and round up to match backend
      const finalTotalAmount = Math.ceil(Math.max(0, itemsSubtotal - discountAmount));

      // Process each item to ensure required fields, including tax_rate_id
      const processedItems = validItems.map(item => {
        // Create a new object without the 'amount' field so backend calculates it
        const { amount, ...itemWithoutAmount } = item;
        
        console.log(`[TAX DEBUG] Processing item with tax_rate_id ${item.tax_rate_id} for backend calculation`);
        
        return {
          ...itemWithoutAmount,
          discount_percent: typeof item.discount_percent === 'number' ? item.discount_percent : 0,
          discount_amount: typeof item.discount_amount === 'number' ? item.discount_amount : 0,
          // Ensure tax_rate_id is included
        };
      });

      const formattedData = {
        ...data, 
        items: processedItems,
        transaction_date: format(data.transaction_date, 'yyyy-MM-dd'),
        due_date: data.due_date ? format(data.due_date, 'yyyy-MM-dd') : null,
        total_amount: Math.round(finalTotalAmount * 100) / 100, 
        is_cancelled: false,
        // Include discount fields
        discount_type: data.discount_type || null,
        discount_value: data.discount_value || null,
      };
      
      console.log(`[TAX DEBUG] Submit data:`, JSON.stringify(formattedData, null, 2));
      
      onSubmit(formattedData as unknown as PurchaseInvoiceFormValues);
    } catch (error) {
      toast.error(`Form submission error: ${(error as Error).message}`);
    }
  };

  // Change default godown
  const handleGodownChange = (godownId: number) => {
    setDefaultGodownId(godownId);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Supplier, Site, Dates, Ref No */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Supplier Selection - Replaced with PartySelector */}
          <FormField
            control={form.control}
            name="party_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Supplier / Vendor *</FormLabel>
                <PartySelector
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                  placeholder="Select or create a supplier..."
                  filterPartyType="supplier"
                  disabled={isSubmitting || isDisabled}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Site Selection */}
          <FormField
            control={form.control}
            name="site_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Site</FormLabel>
                <Select 
                  onValueChange={(value) => field.onChange(parseInt(value))} 
                  value={field.value?.toString()} 
                  disabled={isDisabled || isSubmitting}
                >
                  <FormControl>
                    <SelectTrigger disabled={isDisabled || isSubmitting}>
                      <SelectValue placeholder="Select a site" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {sites.map((site) => (
                      <SelectItem key={site.id} value={site.id.toString()}>
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Transaction Date */}
          <FormField
            control={form.control}
            name="transaction_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                        disabled={isDisabled || isSubmitting}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      disabled={(date) =>
                        date > new Date() || date < new Date("1900-01-01")
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Due Date */}
          <FormField
            control={form.control}
            name="due_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Due Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                        disabled={isDisabled || isSubmitting}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Optional</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value || undefined}
                      onSelect={field.onChange}
                      disabled={(date) =>
                        date < new Date() || date < new Date("1900-01-01")
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Supplier Invoice No (Reference Number) */}
          <FormField
            control={form.control}
            name="reference_number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Supplier Invoice No.</FormLabel>
                <FormControl>
                  <Input 
                    {...field} 
                    placeholder="Enter supplier bill number" 
                    value={field.value || ''} 
                    disabled={isDisabled || isSubmitting} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Default Godown Selection */}
          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium">Default Warehouse</label>
            <Select
              onValueChange={(value) => handleGodownChange(parseInt(value))}
              value={defaultGodownId?.toString() || ''}
              disabled={isDisabled || isSubmitting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select default warehouse" />
              </SelectTrigger>
              <SelectContent>
                {godowns.map((godown) => (
                  <SelectItem key={godown.id} value={godown.id.toString()}>
                    {godown.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              This warehouse will be used as default for new items
            </p>
          </div>
        </div>

        {/* Narration */}
        <FormField
          control={form.control}
          name="narration"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Narration</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter any additional notes"
                  className="min-h-[80px]"
                  disabled={isDisabled || isSubmitting}
                  {...field}
                  value={field.value || ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Items Grid */}
        <Card ref={gridRef} tabIndex={-1}>
          <CardContent className="pt-6">
            <h3 className="text-lg font-medium mb-4">Items</h3>
            <PurchaseInvoiceDataGrid
              columns={columns}
              data={lineItems}
              setData={setLineItems}
              productSearchFn={searchProductsWrapper}
              isLoadingProducts={isLoadingItems}
              disabled={isDisabled || isSubmitting}
              onProductSelect={handleProductSelect}
              siteId={form.watch('site_id')}
              onQuantityChangeForRow={handleQuantityChangeForRow}
            />
          </CardContent>
        </Card>

        {/* Discount Section */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Discount & Total</h3>
              
              {/* Subtotal (Items + Tax) */}
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Subtotal (Items + Tax):</span>
                <span className="font-medium">
                  {new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                  }).format(
                    lineItems.reduce((sum, item) => sum + (parseFloat(String(item.amount || 0))), 0)
                  )}
                </span>
              </div>

              {/* Discount Type and Value */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="discount_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Discount Type</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value === 'none' ? null : value);
                          // Clear discount value when type changes
                          form.setValue('discount_value', null);
                        }}
                        value={field.value || 'none'}
                        disabled={isSubmitting || isDisabled}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="No discount" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">No discount</SelectItem>
                          <SelectItem value="percentage">Percentage (%)</SelectItem>
                          <SelectItem value="fixed">Fixed Amount (₹)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {form.watch('discount_type') && (
                  <FormField
                    control={form.control}
                    name="discount_value"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Discount {form.watch('discount_type') === 'percentage' ? 'Percentage' : 'Amount'}
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              {...field}
                              type="number"
                              step="0.01"
                              min="0"
                              max={form.watch('discount_type') === 'percentage' ? 100 : undefined}
                              placeholder={form.watch('discount_type') === 'percentage' ? "0.00" : "0.00"}
                              value={field.value || ''}
                              onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                              disabled={isSubmitting || isDisabled}
                              className={form.watch('discount_type') === 'percentage' ? "pr-8" : ""}
                            />
                            {form.watch('discount_type') === 'percentage' && (
                              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                                %
                              </span>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Calculated Discount Amount */}
                {form.watch('discount_type') && form.watch('discount_value') && (
                  <div className="flex flex-col space-y-2">
                    <FormLabel>Discount Amount</FormLabel>
                    <div className="text-lg font-medium">
                      {(() => {
                        const subtotal = lineItems.reduce((sum, item) => sum + (parseFloat(String(item.amount || 0))), 0);
                        const discountType = form.watch('discount_type');
                        const discountValue = form.watch('discount_value') || 0;
                        
                        let discountAmount = 0;
                        if (discountType === 'percentage') {
                          discountAmount = (subtotal * discountValue) / 100;
                        } else {
                          discountAmount = discountValue;
                        }
                        
                        return new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                        }).format(discountAmount);
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Total Amount:</span>
                  <span className="text-xl font-semibold tracking-tight">
                    {(() => {
                      const subtotal = lineItems.reduce((sum, item) => sum + (parseFloat(String(item.amount || 0))), 0);
                      const discountType = form.watch('discount_type');
                      const discountValue = form.watch('discount_value') || 0;
                      
                      let discountAmount = 0;
                      if (discountType === 'percentage') {
                        discountAmount = (subtotal * discountValue) / 100;
                      } else if (discountType === 'fixed') {
                        discountAmount = discountValue;
                      }
                      
                      const finalTotal = Math.max(0, subtotal - discountAmount);
                      // Round up to match backend calculation
                      const roundedTotal = Math.ceil(finalTotal);
                      
                      // Update form's total_amount
                      if (form.getValues('total_amount') !== roundedTotal) {
                        form.setValue('total_amount', roundedTotal, { shouldValidate: false });
                      }
                      
                      return new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                      }).format(roundedTotal);
                    })()}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <div className="hidden">{/* Keep old total display hidden for compatibility */}
            Total: {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
            }).format(form.watch('total_amount') || 0)}
          </div>
          <Button 
            type="submit" 
            disabled={isSubmitting || isDisabled}
            onClick={() => {
              console.log('[Submit Button] Clicked');
              console.log('[Submit Button] Form state:', form.formState);
              console.log('[Submit Button] Form errors:', form.formState.errors);
              console.log('[Submit Button] Items errors detail:', JSON.stringify(form.formState.errors.items, null, 2));
            }}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {purchaseInvoice ? 'Updating...' : 'Saving...'}
              </>
            ) : (
              <>{purchaseInvoice ? 'Update' : 'Save'} Purchase Invoice</>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}; 